import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, Switch } from 'react-router-dom';
import useDocMembershipTransferOver from '@apis/useDocMembershipTransferOverApi';
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Typography } from "@curves/ui-base";
import MembershipTransferOverContent from '@icoach/documents/membership/transferOver/MembershipTransferOverContent';
import MembershipTransferOverHistory from '@icoach/documents/membership/transferOver/MembershipTransferOverHistory';
import { DOC_MEMBERSHIP_TRANSFEROVER_LINK } from '@icoach/documents/membership/staticData';
import ConfirmTemporaryContractDialog from '@icoach/documents/dialog/ConfirmTemporaryContractDialog';
import { BlankFormBox, SearchBar } from '@icoach/documents/components/';
import { CLUB_DOCUMENTS_ROUTER } from '@icoach/router/routerPath';

const MembershipTransferOverPage = (props) => {
    const { getInitTransferOverOptionApi, getDocTemporaryTransferOverContractApi, deleteDocTemporaryTransferOverContractApi } = useDocMembershipTransferOver();
    const history = useHistory();
    const { peopleType: peopleTypeProps, referenceID: referenceIDProps } = useParams();
    const [options, setOptions] = useState({});
    const [openDialog, setOpenDialog] = useState(false); //有暫存資料時的彈出視窗
    const [isMounted, setMounted] = useState(false);
    const searchRef = useRef([]);
    const temporaryIDRef = useRef(0);

    const checkPeopleType = (opts) => {
        const { targetOptions = [] } = opts;
        const isInclude = targetOptions.find(({ value }) => String(peopleTypeProps) === String(value));
        const defaultPeopleType = 2; // 預設值 會員
        if (!peopleTypeProps || !isInclude) {
            history.replace(CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.GET_PEOPLETYPE_PATH(defaultPeopleType));
        }
    };

    // 重設 按鈕
    const handleResetClick = (peopleType) => {
        const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.GET_PEOPLETYPE_PATH(peopleType);
        history.replace(path);
    };

    // 搜尋 按鈕
    const handleSearchHistoryClick = (id) => {
        const { targetID, targetType } = searchRef.current.getResult();
        const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.GET_HISTORY_PATH(targetType, targetID);
        history.replace(path);
    };

    // 新增 按鈕
    const handleCreateClick = () => {
        const { targetID: id, targetType: type } = searchRef.current.getResult();
        const params = { id, type };
        doCreateBlankContractApi(params, type, id);
    };

    // 彈出視窗 要刪除暫存資料
    const handleDialogNoClick = () => {
        const params = {
            id: referenceIDProps,
            type: peopleTypeProps,
        };
        doDeleteTemporaryDataApi(params);
    };

    // 彈出視窗 讀取上次暫存資料繼續編輯
    const handleDialogYseClick = () => {
        if (!temporaryIDRef.current) return;
        const contractID = temporaryIDRef.current;
        const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.GET_CONTRACT_PATH(peopleTypeProps, referenceIDProps, contractID);
        setOpenDialog(false);
        history.push(path);
    };

    // 刪除暫存合約資料
    const doDeleteTemporaryDataApi = async (parmas) => {
        const resp = await deleteDocTemporaryTransferOverContractApi(parmas);
        if (resp) {
            const contractID = 0;
            const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.GET_CONTRACT_PATH(peopleTypeProps, referenceIDProps, contractID);
            setOpenDialog(false);
            history.push(path);
        }
    };

    // 新增空白頁面
    const doCreateBlankContractApi = async (params, peopleType, referenceID) => {
        let temporaryID;
        temporaryIDRef.current = 0;
        temporaryID = await getDocTemporaryTransferOverContractApi(params);
        if (temporaryID !== 0 && !!temporaryID) {
            temporaryIDRef.current = temporaryID;
            setOpenDialog(true);
        } else {
            const contractID = 0;
            const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.GET_CONTRACT_PATH(peopleType, referenceID, contractID);
            history.push(path);
        }
    };

    const useSearchBarMemo = useMemo(
        () => {
            return (
                <SearchBar
                    ref={searchRef}
                    className="mb-4"
                    sourceOption={options.targetOptions}
                    onResetEvent={handleResetClick}
                    onCreateEvent={handleCreateClick}
                    onSearchHistoryEvent={handleSearchHistoryClick}
                    routerPath={CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.BASE}
                />
            );
        },
        // eslint-disable-next-line
        [options]
    );

    // 初始search options
    const doInitOptionsApi = async () => {
        const opts = await getInitTransferOverOptionApi();
        if (opts) {
            checkPeopleType(opts);
            setOptions(opts);
        }
        setMounted(true);
    };

    useEffect(
        () => {
            doInitOptionsApi();
            return () => setMounted(false);
        },
        // eslint-disable-next-line
        []
    );

    return (
        <div className="container main-container-spacing doc-membership-transferover">
            {isMounted && (
                <React.Fragment>
                    <header className={'page-header'}>
                        <Typography className={'page-header-title'} variant={'h3'}>
                            會籍轉讓申請書
                        </Typography>
                        <div className="btn-group">
                            <BlankFormBox sourceData={DOC_MEMBERSHIP_TRANSFEROVER_LINK} />
                        </div>
                    </header>
                    {useSearchBarMemo}
                    <Switch>
                        <PrivateRoute
                            exact
                            path={CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.HISTORY}
                            as={MembershipTransferOverHistory}
                            onBlankContractClick={handleCreateClick}
                            onReturnHistory={handleSearchHistoryClick}
                        />
                        <PrivateRoute
                            exact
                            path={CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.CONTRACT}
                            as={MembershipTransferOverContent}
                            onReturnHistory={handleSearchHistoryClick}
                        />
                        <PrivateRoute
                            exact
                            path={CLUB_DOCUMENTS_ROUTER.TRANSFEROVER.READONLY_CONTRACT}
                            as={MembershipTransferOverContent}
                            isReadOnly={true}
                            onReturnHistory={handleSearchHistoryClick}
                        />
                    </Switch>
                    <ConfirmTemporaryContractDialog
                        open={openDialog}
                        handleClose={() => setOpenDialog(false)}
                        handleClickYes={handleDialogYseClick}
                        handleClickNo={handleDialogNoClick}
                    />
                </React.Fragment>
            )}
        </div>
    );
};

export default MembershipTransferOverPage;
